import {Signal, signal} from "@angular/core";
import {catchError, concat, defer, EMPTY, finalize, Observable, Subject, takeUntil, tap} from "rxjs";
import {finnMimeinfoForFil, Mimeinfo} from "../../støttefunksjoner/mimetyper";
import {erIkkeNil, erNil} from "../../støttefunksjoner/nilsjekk";
import {HttpErrorResponse, HttpEvent, HttpEventType, HttpProgressEvent} from "@angular/common/http";
import {initDebugger} from "../../støttefunksjoner/debug";
import {verifiserErIkkeNil} from "../../støttefunksjoner/antakelser";
import {disjunksjon, fjernFraListeInline, første} from "../../støttefunksjoner/lister";
import {OpplastetDokumentVersjonValideringsfeil} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {erHttpErrorResponse, finnValideringsfeilMedFeilkode} from "felles-ng2/infrastruktur/http/httpErrorResponseTyper";
import {erStreng} from "felles-ng2/støttefunksjoner/strenger";
import {GoogleDriveFilvelgerGui} from "web-ng2/delt/komponenter/filvelger-skytjenester/modale/googledrive-filvelger-dialog/GoogleDriveFilvelgerGui";
import {filendelseFraFile} from "felles-ng2/støttefunksjoner/filer";

const debug = initDebugger('SekvensiellFilopplasting');

/**
 * Håndterer sekvensiell opplasting av filer.
 * Laster angitte filer opp i sekvens, dvs en av gangen.
 * Filer som er for store, som ikke har gyldig mimetype eller som ikke kan lese vil ikke bli lastet opp,
 * se {@link #valider()}
 *
 * Nye filer kan legges til etter opprettelse, se {@link #leggTilFiler} og {@link #leggTilNyDokumentversjon}.
 * Filer som legges til mens en opplasting pågår blir lastet opp fortløpende av den pågående opplastingen,
 * dvs at man ikke trenger (og ikke skal) kalle {@link #start()} på nytt for disse.
 */
export class SekvensiellFilopplasting<M> {
    /** Maksimalt antall filer som kan lastes opp i en SekvensiellFilopplasting-operasjon */
    static maksAntallFiler = 10;

    /** Opplasting-representasjon av alle filer lagt til for opplasting */
    opplastinger: Opplasting<M>[] = [];

    /** Opplastinger som har passert validering og som er klare for å starte opplasting */
    private validerteOpplastinger: Opplasting<M>[] = [];
    private readonly _ikkeFerdigeSomSignal = signal<Opplasting<M>[]>([]);
    /** Sann fra opplasting startes (subscribe på observable levert av {@link #start()}) og til denne er fullført */
    private opplastingPågår = false;

    /** Oppretter en opplasting uten filer, filene legges til med {@link #leggTilFiler}/{@link #leggTilNyDokumentversjon} */
    static tom<M>(avhengigheter: FilopplastingAvhengigheter<M>): SekvensiellFilopplasting<M> {
        return new SekvensiellFilopplasting(avhengigheter);
    }

    private constructor(private readonly avhengigheter: FilopplastingAvhengigheter<M>) {
    }

    /** Legger til angitte filer, og returnerer et {@link Opplastinger} med valideringsresultatet for disse filene.
     * Filene lastes opp av pågående opplasting dersom en slik pågår ({@link #opplastingPågårNå}),
     * ellers lastes de opp ved neste kall til {@link #start()}.
     * Bruk {@link Opplastinger#fjern} dersom filene likevel ikke skal lastes opp, feks fordi de har valideringsfeil.
     * Merk at {@link #maksAntallFiler} gjelder for alle filene i opplastingen, dvs også de som allerede er lastet opp. */
    leggTilFiler(filer: File[]): Promise<Opplastinger<M>> {
        return this.leggTil(filer.map(fil => new Opplasting<M>(fil, this.avhengigheter)));
    }

    /** Legger til angitt fil som ny versjon av angitt eksisterende dokument, se forøvrig {@link #leggTilFiler} */
    leggTilNyDokumentversjon(fil: File, eksisterende: EksisterendeDokument<M>): Promise<Opplastinger<M>> {
        return this.leggTil([new Opplasting<M>(fil, filopplastingAvhengigheterMedStatiskEksisterende<M>(this.avhengigheter, eksisterende))]);
    }

    /** Sann dersom opplasting er startet og ikke fullført, dvs at filer som legges til blir lastet opp uten nytt kall til {@link #start()} */
    get opplastingPågårNå(): boolean {
        return this.opplastingPågår;
    }

    /** Returnerer liste med alle ikke-ferdige opplastinger,
     * dvs alle unntatt opplastinger med status 'ferdig' eller 'feilet', eller der validering feilet.
     * Merk at listen vil være tom inntil {@link #valider()} har blitt kalt og har returnert. */
    ikkeFerdige(): Opplasting<M>[] {
        return this.validerteOpplastinger.filter(opplasting => !['ferdig', 'feilet'].includes(opplasting.status()));
    }

    /** Signal som oppdateres hver gang en opplasting ferdigstilles,
     * leverer alle validerte, ikke-ferdige opplastinger, dvs alle unntatt opplastinger med status 'ferdig' eller 'feilet', eller der validering feilet.*/
    get ikkeFerdigeSomSignal(): Signal<Opplasting<M>[]> {
        return this._ikkeFerdigeSomSignal;
    }

    /** Returnerer observable som starter sekvensiell opplasting av filer når denne subscribes til.
     * Filer som legges til underveis blir lastet opp fortløpende, se {@link #leggTilFiler}.
     * Dersom en opplasting allerede pågår fullføres returnert observable umiddelbart uten å laste opp noe,
     * da vil filene bli lastet opp av den pågående opplastingen. */
    start(): Observable<HttpEvent<M>> {
        return defer(() => {
            if (this.opplastingPågår) {
                debug('opplasting pågår allerede, ventende filer lastes opp av denne');
                return EMPTY;
            }
            this.opplastingPågår = true;
            debug(`starter opplasting av ${this.ventende().length} filer`, this.ventende());
            return this.lastOppVentende()
              .pipe(finalize(() => {
                  this.opplastingPågår = false;
                  this.opplastinger = [];
                  this.validerteOpplastinger = [];
                  this.oppdaterIkkeFerdigeSignal();
              }));
        });
    }

    avbryt() {
        this.opplastinger.forEach(opplasting => opplasting.avbryt())
    }

    //private hjelpemetoder

    /** Laster opp neste ventende opplasting, og deretter (rekursivt) den neste igjen, inntil det ikke er flere ventende.
     * Merk at ventende opplastinger leses på nytt (defer) etter hver fullførte opplasting,
     * slik at filer som legges til underveis også blir lastet opp. */
    private lastOppVentende(): Observable<HttpEvent<M>> {
        return defer(() => {
            const neste = første(this.ventende());
            if (erNil(neste)) return EMPTY;
            return concat(
              neste.lastOpp()
                .pipe(
                  //sørg for å oppdatere ikkeFerdige signal ved HttpEventType.Response - det er ved denne hendelsen at ArkivDatatjeneste oppdatere sitt lager
                  //og dermed publiserer 'nytt dokument' til event abonnenter
                  tap(httpEvent => (httpEvent.type == HttpEventType.Response) ? this.oppdaterIkkeFerdigeSignal() : null),
                  //sørg for å oppdatere ikkeFerdige signal ved 'ferdig opplastet'/'error'
                  finalize(() => this.oppdaterIkkeFerdigeSignal())),
              this.lastOppVentende());
        });
    }

    /** Validerte opplastinger som ikke har blitt startet, dvs som verken er under opplasting, fullført, feilet eller avbrutt */
    private ventende(): Opplasting<M>[] {
        return this.validerteOpplastinger.filter(opplasting => opplasting.status() == 'venter');
    }

    private oppdaterIkkeFerdigeSignal() {
        this._ikkeFerdigeSomSignal.set(this.ikkeFerdige())
    }

    private async leggTil(nyeOpplastinger: Opplasting<M>[]): Promise<Opplastinger<M>> {
        this.opplastinger.push(...nyeOpplastinger);
        const validering = await this.valider(nyeOpplastinger);
        return {validering, opplastinger: nyeOpplastinger, fjern: () => this.fjern(nyeOpplastinger)};
    }

    /** Fjerner angitte opplastinger, avbryter først opplastingen dersom en av filene er under opplasting */
    private fjern(opplastingerSomFjernes: Opplasting<M>[]) {
        debug(`fjerner ${opplastingerSomFjernes.length} opplastinger`, opplastingerSomFjernes);
        opplastingerSomFjernes.forEach(opplasting => opplasting.avbryt());
        fjernFraListeInline(this.opplastinger, opplasting => opplastingerSomFjernes.includes(opplasting));
        fjernFraListeInline(this.validerteOpplastinger, opplasting => opplastingerSomFjernes.includes(opplasting));
        this.oppdaterIkkeFerdigeSignal();
    }

    /** Validerer angitte opplastinger, legger de gyldige til i {@link #validerteOpplastinger} slik at de blir lastet opp */
    private async valider(opplastingerTilValidering: Opplasting<M>[]): Promise<Valideringsresultat> {
        const valideringsresultat: Valideringsresultat = valideringsresultatOk();
        if (this.opplastinger.length > SekvensiellFilopplasting.maksAntallFiler) {
            valideringsresultat.forMangeFiler = true;
            valideringsresultat.harFeil = true;
        }

        for (const opplasting of opplastingerTilValidering) {
            const validering = await opplasting.validering;
            if (validering.forStor) valideringsresultat.forStoreFiler.push(opplasting.fil);
            if (validering.ugyldigFiltype) valideringsresultat.ugyldigeFiltyper.push(opplasting.fil);
            if (validering.ikkeLesbar) valideringsresultat.ikkeLesbareFiler.push(opplasting.fil);
            if (validering.erSendtTilSignering) valideringsresultat.nyeVersjonerForFilerSendtTilSignering.push(opplasting.fil);
            if (validering.erGoogleDocsFiltype) valideringsresultat.googleDocsFiler.push(opplasting.fil);
            if (!validering.erGyldig) valideringsresultat.harFeil = true;

            if (opplasting.erNyVersjon) valideringsresultat.nyeVersjoner.push(opplasting.fil);
            if (validering.erGyldig) this.validerteOpplastinger.push(opplasting);
        }
        valideringsresultat.okFiler = disjunksjon(opplastingerTilValidering.map(somFil), [...valideringsresultat.forStoreFiler, ...valideringsresultat.ugyldigeFiltyper, ...valideringsresultat.ikkeLesbareFiler, ...valideringsresultat.nyeVersjonerForFilerSendtTilSignering]);

        debug(`validering harFeil:${valideringsresultat.harFeil}`, valideringsresultat)
        this.oppdaterIkkeFerdigeSignal();
        return valideringsresultat
    }

}

/** Håndterer opplasting av en enkelt fil */
export class Opplasting<M> {
    status = signal<'venter' | 'lasteropp' | 'ferdig' | 'feilet' | 'avbrutt'>('venter');
    /** tall mellom 0 og 100 som angir hvor langt opplastingen har kommet */
    progresjon = signal(0);
    readonly mimeinfo: Mimeinfo | null;

    /** Settes til eksisterende dokument med samme navn, dersom slikt finnes, null ellers.
     * Se {@link FilopplastingAvhengigheter#eksisterende}*/
    readonly eksisterendeVersjon: EksisterendeDokument<M> | null;

    /** Resultat fra validering av denne opplasting */
    private readonly valideringProm: Promise<Valideringsfeil>;

    /** resultatet fra validering, MERK først satt når valideringProm har resolvet */
    private valideringsresultat: Valideringsfeil | null = null;
    private terminer = new Subject<void>();

    constructor(public readonly fil: File, private readonly avhengigheter: FilopplastingAvhengigheter<M>) {
        this.mimeinfo = finnMimeinfoForFil(fil);
        this.eksisterendeVersjon = this.avhengigheter.eksisterende(this.fil.name);
        this.valideringProm = this.valider();
    }

    /** Resultat fra validering av denne opplasting, opplasting bør forhindres dersom ikke {@link Valideringsfeil#erGyldig}*/
    get validering() {
        return this.valideringProm;
    }

    get erNyVersjon(): boolean {
        return erIkkeNil(this.eksisterendeVersjon);
    }

    lastOpp(): Observable<HttpEvent<M>> {
        return this.lastOppOpr()
          .pipe(
            takeUntil(this.terminer),
            catchError(e => this.håndterRequestFeil(e)),
            tap({
                subscribe: () => this.håndterRequestInitiert(),
                next: hendelse => this.håndterRequestProgresjonOgRespons(hendelse)
            })
          );
    }

    avbryt() {
        this.status.set('avbrutt');
        this.terminer.next();
    }

    errorLogInfo(): string {
        return JSON.stringify({
            fileIkkeNull: this.fil != null,
            stoerrelse: this.fil?.size,
            type: this.mimeinfo,
            filnavn: this.fil?.name
        });
    }

    //private hjelpemetoder

    private async valider(): Promise<Valideringsfeil> {
        const valideringsfeil = {
            erGyldig: true,
            forStor: this.fil.size > this.avhengigheter.maksFilstørrelse,
            ugyldigFiltype: erNil(this.mimeinfo),
            ikkeLesbar: false,
            erSendtTilSignering: this.eksisterendeVersjon?.erSendtTilSignering ?? false,
            erGoogleDocsFiltype: this.erGoogleDocsFiltype()
        };

        //sjekker kun lesbarhet dersom fil ikke er ugyldig pga andre tester
        if (!valideringsfeil.forStor && !valideringsfeil.ugyldigFiltype)
            valideringsfeil.ikkeLesbar = !(await this.avhengigheter.filKanLeses(this.fil));

        valideringsfeil.erGyldig = !valideringsfeil.forStor && !valideringsfeil.ugyldigFiltype && !valideringsfeil.ikkeLesbar && !valideringsfeil.erSendtTilSignering;

        this.setValideringsresultat(valideringsfeil);
        return valideringsfeil;
    }

    private erGoogleDocsFiltype():boolean {
        return GoogleDriveFilvelgerGui.googleDocsFilendinger.includes(filendelseFraFile(this.fil));
    }

    //public for testbarhet
    setValideringsresultat(feil: Valideringsfeil) {
        this.valideringsresultat = feil;
    }

    private lastOppOpr() {
        verifiserErIkkeNil(this.valideringsresultat, 'har du glemt å kalle valider og vente på svar før opplasting ble startet?')
        if (this.status() == 'avbrutt') return EMPTY;
        if (!this.valideringsresultat!.erGyldig) return EMPTY;

        return erIkkeNil(this.eksisterendeVersjon) ?
          this.avhengigheter.registrerNyVersjon(this.fil, this.eksisterendeVersjon.dokument) :
          this.avhengigheter.registrerNy(this.fil);
    }

    private håndterRequestInitiert() {
        this.status.set('lasteropp');
    }

    private håndterRequestProgresjonOgRespons(httpHendelse: HttpEvent<M>) {
        if (httpHendelse.type == HttpEventType.UploadProgress) {
            const progresjon = httpHendelse as HttpProgressEvent;
            this.progresjon.set(Math.floor(100.0 * progresjon.loaded / (progresjon.total ?? 1)));
        }
        if (httpHendelse.type == HttpEventType.Response) {
            this.progresjon.set(1);
            this.status.set('ferdig');
        }
    }

    private håndterRequestFeil(e: any) {
        this.status.set('feilet');
        this.avhengigheter.vedOpplastingFeilet(this.feilSomOpplastingsfeil(e), this);
        //vi svelger feilen og fortsetter slik at påfølgende opplastinger kan gjennomføres
        return EMPTY;
    }

    private feilSomOpplastingsfeil(feil: any): Opplastingsfeil {
        if (erHttpErrorResponse(feil)) {
            const ugyldigMimetype = finnValideringsfeilMedFeilkode(feil, OpplastetDokumentVersjonValideringsfeil.ugyldig_mimeformat);
            const erZscalarFeil = feil.status == 403 && erStreng(feil.error) && feil.error.includes('zscaler')
            return {httpFeil: feil, ugyldigMimetype: ugyldigMimetype?.data.mimetype ?? undefined, erZscalarFeil};
        }
        return {generellFeil: feil, erZscalarFeil: false};
    }
}

/** Filene som ble lagt til i en {@link SekvensiellFilopplasting}, se {@link SekvensiellFilopplasting#leggTilFiler} */
export interface Opplastinger<M> {
    /** Valideringsresultat for filene som ble lagt til */
    validering: Valideringsresultat;
    /** Opplastingene som ble lagt til */
    opplastinger: Opplasting<M>[];
    /** Fjerner de tillagte filene fra opplastingen slik at de ikke blir lastet opp,
     * og avbryter opplastingen dersom en av filene allerede er under opplasting. */
    fjern(): void;
}

export type Opplastingsfeil = {
    ugyldigMimetype?: string;
    httpFeil?: HttpErrorResponse;
    generellFeil?: any;
    /** Sann dersom opplastingen ble stoppet av zScalar - en proxy som bla Eika-bankene benytter for å overvåke deling av dokumenter */
    erZscalarFeil: boolean
}

export function valideringsresultatOk(): Valideringsresultat {
    return {
        harFeil: false,
        forMangeFiler: false,
        nyeVersjoner: [],
        forStoreFiler: [],
        ugyldigeFiltyper: [],
        ikkeLesbareFiler: [],
        googleDocsFiler: [],
        nyeVersjonerForFilerSendtTilSignering: [],
        okFiler: []
    }
}

export interface FilopplastingAvhengigheter<M> {
    maksFilstørrelse: number;

    /** Finner eventuell eksisterende fil med angitt (fil)navn. */
    eksisterende(name: string): EksisterendeDokument<M> | null;

    /** Sann dersom angitt fil kan leses.
     * Brukes for å verifisere at fil kan lastes opp, returnerer feks null dersom man har valgt en fil fra OneDrive som ikke lastet ned til lokal disk. */
    filKanLeses(fil: File): Promise<boolean>;

    /** Utfører server-kall for opplasting og registrering av nytt dokument.
     * Merk at vi forventer at returnert Observable propagerer eventuelle feil. */
    registrerNy(fil: File): Observable<HttpEvent<M>>;

    /** Utfører server-kall for opplasting og registrering av ny dokument-versjon.
     * Merk at vi forventer at returnert Observable propagerer eventuelle feil. */
    registrerNyVersjon(fil: File, eksisterendeVersjon: M): Observable<HttpEvent<M>>;

    /** Blir kalt dersom feil oppstår ved registrering (opplasting) av dokument.
     * Ved {@link OpplastetDokumentVersjonValideringsfeil} er ytterligere informasjon
     * tilgjengelig i 'feil', herunder informasjon om eventuell ugyldig mimetype. */
    vedOpplastingFeilet(feil: Opplastingsfeil, opplasting: Opplasting<M>): void;
}

export interface EksisterendeDokument<M> {
    dokument: M,
    erSendtTilSignering: boolean
}

/** Returnerer angitt delgat, men endrer {@link FilopplastingAvhengigheter#eksisterende} slik at denne alltid returnerer angitt eksisterende */
function filopplastingAvhengigheterMedStatiskEksisterende<M>(delegat: FilopplastingAvhengigheter<M>, eksisterende: EksisterendeDokument<M>): FilopplastingAvhengigheter<M> {
    return {
        maksFilstørrelse: delegat.maksFilstørrelse,
        eksisterende: name => eksisterende,
        registrerNy: fil => delegat.registrerNy(fil),
        registrerNyVersjon: (fil, eksisterendeVersjon) => delegat.registrerNyVersjon(fil, eksisterendeVersjon),
        vedOpplastingFeilet: (feil, opplasting) => delegat.vedOpplastingFeilet(feil, opplasting),
        filKanLeses: fil => delegat.filKanLeses(fil)
    }
}

export interface Valideringsresultat {
    harFeil: boolean;
    forMangeFiler: boolean;
    ugyldigeFiltyper: File[];
    forStoreFiler: File[];
    ikkeLesbareFiler: File[];
    googleDocsFiler: File[];
    /** det er ikke mulig å registrere ny versjon på dokument som har blitt sendt til signering */
    nyeVersjonerForFilerSendtTilSignering: File[];
    /** filer som vil bli registrert som nye versjoner, vil være et subset av okFiler */
    nyeVersjoner: File[];
    /** alle filene som ikke har valideringsfeil */
    okFiler: File[];
}

export interface Valideringsfeil {
    erGyldig: boolean;
    forStor: boolean;
    ugyldigFiltype: boolean;
    ikkeLesbar: boolean;
    erSendtTilSignering: boolean;
    erGoogleDocsFiltype: boolean;
}

function somFil<M>(opplasting: Opplasting<M>) {
    return opplasting.fil;
}
